import { SuperMemoGrade, KnowledgeItem } from '../types/learning';

/**
 * Represents the result of a learning challenge for grading purposes.
 * Extend this interface as needed for new challenge types.
 */
export interface ChallengeResult {
  correct: boolean;
  attempts: number;
  usedHint?: boolean;
  usedShowAnswer?: boolean;
  time?: number; // in seconds
  [key: string]: any; // Allow extension for mission-specific data
}

/**
 * Maps sequence challenge score bands to SM-2 grade (0-5).
 * Uses standard mission thresholds:
 * 95/85/70/60/40 => 5/4/3/2/1, else 0.
 */
export function mapSequenceScoreToSuperMemoGrade(score: number): SuperMemoGrade {
  if (score >= 95) return 5;
  if (score >= 85) return 4;
  if (score >= 70) return 3;
  if (score >= 60) return 2;
  if (score >= 40) return 1;
  return 0;
}

/**
 * Default mapping from challenge result to SuperMemo grade (0-5).
 * This can be overridden per mission or challenge type by passing a custom gradeMapper.
 * @param result The challenge result
 * @returns SuperMemoGrade (0-5)
 */
export function mapResultToSuperMemoGrade(result: ChallengeResult): SuperMemoGrade {
  if (!result.correct) return 0;
  if (result.usedShowAnswer) return 0;
  if (result.attempts === 1 && result.time !== undefined && result.time < 5 && !result.usedHint) return 5;
  if (result.attempts === 1 && !result.usedHint) return 4;
  if (result.attempts === 1 && result.usedHint) return 3;
  if (result.attempts === 2) return 2;
  if (result.attempts > 2) return 1;
  return 0;
}

/**
 * Example: Custom grading function for a specific mission type.
 * @param result The challenge result
 * @returns SuperMemoGrade (0-5)
 */
export function customMissionGradeMapper(result: ChallengeResult): SuperMemoGrade {
  // Example: Penalize time more heavily
  if (!result.correct) return 0;
  if (result.time !== undefined && result.time > 20) return 1;
  if (result.attempts === 1 && !result.usedHint) return 5;
  if (result.attempts === 1 && result.usedHint) return 4;
  if (result.attempts === 2) return 3;
  if (result.attempts > 2) return 2;
  return 0;
}

/**
 * Shared DRY utility for handling challenge completion in learning components.
 * Calls the correct onComplete and legacy callbacks with the computed grade.
 *
 * @param params - Challenge completion parameters
 * @example
 * handleChallengeComplete({
 *   success, score, total, movesUsed, gradeMapper, onComplete, onCompleteLegacy, extraResultFields: { time, usedHint }
 * });
 */
export function handleChallengeComplete({
  success,
  score,
  total,
  movesUsed,
  gradeMapper,
  onComplete,
  onCompleteLegacy,
  extraResultFields = {}
}: {
  success: boolean;
  score: number;
  total: number;
  movesUsed?: number;
  gradeMapper?: (result: ChallengeResult) => SuperMemoGrade;
  onComplete?: (grade: SuperMemoGrade) => void;
  onCompleteLegacy?: (...args: any[]) => void;
  extraResultFields?: Partial<ChallengeResult>;
}) {
  const result: ChallengeResult = {
    correct: success,
    attempts: score,
    ...extraResultFields
  };
  const grade = (gradeMapper ?? mapResultToSuperMemoGrade)(result);
  if (onComplete) onComplete(grade);
  if (onCompleteLegacy) onCompleteLegacy(success, score, total, movesUsed);
}

/**
 * Usage:
 * - Pass `mapResultToSuperMemoGrade` as the default gradeMapper to challenge components.
 * - For mission-specific grading, pass a custom function via the `gradeMapper` prop.
 */ 
